
import { ReactNode, useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';

interface ProtectedRouteProps {
  children: ReactNode;
  requiredRole: 'admin' | 'employee' | 'workspace_user';
}

export const ProtectedRoute = ({ children, requiredRole }: ProtectedRouteProps) => {
  const [isLoading, setIsLoading] = useState(true);
  const [isAuthorized, setIsAuthorized] = useState(false);

  useEffect(() => {
    const checkAuth = async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        
        if (!session) {
          setIsAuthorized(false);
          setIsLoading(false);
          return;
        }
        
        // For employee role, check if user has an employee profile
        if (requiredRole === 'employee') {
          const { data: profile } = await supabase
            .from('employee_profiles')
            .select('id, is_admin')
            .eq('user_id', session.user.id)
            .single();
          
          setIsAuthorized(!!profile);
        }
        // For admin role, check if user is an admin in employee_profiles
        else if (requiredRole === 'admin') {
          const { data: profile } = await supabase
            .from('employee_profiles')
            .select('is_admin')
            .eq('user_id', session.user.id)
            .eq('is_admin', true)
            .single();
          
          setIsAuthorized(!!profile);
        }
        // For workspace user, just check if they're authenticated
        else if (requiredRole === 'workspace_user') {
          setIsAuthorized(true);
        }
      } catch (error) {
        console.error('Auth check error:', error);
        setIsAuthorized(false);
      } finally {
        setIsLoading(false);
      }
    };
    
    checkAuth();
  }, [requiredRole]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!isAuthorized) {
    toast.error(`Access denied: ${requiredRole} role required`);
    return <Navigate to="/auth" replace />;
  }

  return <>{children}</>;
};
